import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Switch } from '@/components/ui/switch'
import { useTRPC } from '@/trpc/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Clock } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/permanent_business_settings/')({
  component: RouteComponent,
})

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()

  const { data: settings, isPending: isPendingSettings } = useQuery({
    ...api.permanentBusinessSettings.getPermanentBusinessSettings.queryOptions(),
  })

  const updateMutation = useMutation({
    ...api.permanentBusinessSettings.updatePermanentBusinessSettings.mutationOptions({
      onSuccess: () => {
        toast.success('Permanent business settings updated successfully')
        queryClient.invalidateQueries({
          queryKey: api.permanentBusinessSettings.getPermanentBusinessSettings.queryKey(),
        })
      },
      onError: (error) => {
        console.error('Error updating permanent business settings:', error)
        toast.error('Failed to update permanent business settings')
      },
    }),
  })

  const form = useForm({
    defaultValues: {
      requireLocation: false,
      requireSelfie: false,
      enforceDoleHolidayPayRules: true,
      calculateRegionalHolAsSpecialHol: true,
    },
  })

  useEffect(() => {
    if (settings) {
      form.reset({
        requireLocation: settings.requireLocation ?? false,
        requireSelfie: settings.requireSelfie ?? false,
        enforceDoleHolidayPayRules: settings.enforceDoleHolidayPayRules ?? true,
        calculateRegionalHolAsSpecialHol: settings.calculateRegionalHolAsSpecialHol ?? true,
      })
    }
  }, [settings, form])

  function onSubmit(values: {
    requireLocation: boolean
    requireSelfie: boolean
    enforceDoleHolidayPayRules: boolean
    calculateRegionalHolAsSpecialHol: boolean
  }) {
    updateMutation.mutate({
      requireLocation: values.requireLocation,
      requireSelfie: values.requireSelfie,
      enforceDoleHolidayPayRules: values.enforceDoleHolidayPayRules,
      calculateRegionalHolAsSpecialHol: values.calculateRegionalHolAsSpecialHol,
    })
  }

  return (
    <DashboardShell>
      <DashboardHeader
        heading="Permanent Business Settings"
        text="Configure permanent settings that apply to your business."
      />

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <CardTitle className="text-lg">Clock In/Out Requirements</CardTitle>
            </div>
            <CardDescription>
              Specify which requirements are mandatory for employees to clock in/out. If a toggle
              is off, the requirement is optional.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="requireLocation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Require Location</FormLabel>
                      <FormControl>
                        <div className="flex items-center justify-between">
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="requireSelfie"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Require Selfie</FormLabel>
                      <FormControl>
                        <div className="flex items-center justify-between">
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="enforceDoleHolidayPayRules"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Enforce DOLE Holiday Pay Rules</FormLabel>
                      <FormControl>
                        <div className="flex items-center justify-between">
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </div>
                      </FormControl>
                      <FormMessage />
                      <FormDescription>
                        When enabled, employees must work or have paid leave on the immediately
                        preceding workday to receive holiday pay for regular holidays. If absent
                        without pay on the preceding workday, holiday pay is set to 0.
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="calculateRegionalHolAsSpecialHol"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Calculate Regional Hol as Special Hol</FormLabel>
                      <FormControl>
                        <div className="flex items-center justify-between">
                          <Switch checked={field.value} onCheckedChange={field.onChange} />
                        </div>
                      </FormControl>
                      <FormMessage />
                      <FormDescription>
                        When enabled, regional holiday pay is merged into special holiday columns
                        in payroll, timesheet, and payslip exports. When disabled, regional
                        holidays appear as separate columns/rows.
                      </FormDescription>
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  disabled={updateMutation.isPending || isPendingSettings}
                  className="w-full"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  )
}
